import { BotonPreguntaleAtlas } from "../../chat-atlas";
import { useBrandProfile } from "../../hooks/useBrandProfile";
import { CanalesReferencia } from "./components/CanalesReferencia";
import { SeccionConexiones } from "./components/SeccionConexiones";
import { useFuentesConectadas } from "./datos/fuentesConectadas";

/**
 * "Fuentes de datos": de dónde saca Atlas lo que sabe.
 *
 * El título es el MISMO que el ítem de la sidebar ("Fuentes de datos"): decía
 * "Datos" y era el único lugar de la app donde la pantalla se llamaba distinto
 * según por dónde se llegara.
 *
 * Es el destino de los TRES nodos de fuente del mapa (`instagram`, `meta_ads`,
 * `tiendanube`). Antes iban a Insights, que es donde viven los NÚMEROS: clickear
 * "Tiendanube" para caer en un tablero de métricas de ejemplo no contestaba la
 * pregunta que el nodo hace ("¿esto está conectado?"). El nodo `numeros`
 * (Métricas de tu marca) sigue yendo a Insights, que es su lugar
 * (ADR-2026-08-17-estudio-marketing-nodos-fuente-a-datos).
 *
 * NO lleva `<BannerVistaEjemplo />`, por el mismo motivo que `MarcaPage`: ese
 * cartel avisa que un número no es de la cuenta del cliente, y acá no hay
 * ningún número de ejemplo. Las conexiones son el estado REAL de la cuenta y la
 * base de conocimiento tiene su propio cartel, que dice algo distinto (dónde
 * queda guardado lo que se carga y qué se hace con eso todavía).
 *
 * Dos secciones:
 *  1. Conexiones — las tres fuentes y su estado real (`useFuentesConectadas`).
 *  2. Base de conocimiento — hoy un solo bloque, "Canales de referencia".
 */

export const ConfiguracionDatosPage = () => {
  const { profile, currentBrandId, loading, loadingBrands, refresh } = useBrandProfile();
  const fuentes = useFuentesConectadas(profile?.id_company ?? null, currentBrandId);

  const cargandoPerfil = loading || loadingBrands;
  /**
   * El perfil terminó de cargar y quedó en `null`: `fetchProfile` se traga
   * TODOS los errores (`catch { }` y dos `return` tempranos antes de
   * `setProfile`), así que una red caída, una sesión vencida o RLS rota en
   * `profiles` dejan `profile === null` con `loading: false`.
   *
   * SIN ESTO la pantalla mentía: con `id_company` en `null`,
   * `useFuentesConectadas` ni siquiera consulta `instagram_data`, así que no hay
   * `error` de Supabase que reportar y la tarjeta caía en "Sin conectar" con el
   * botón "Conectar Instagram" — mandando a reconectar por OAuth algo que puede
   * estar andando, que es exactamente lo que esta pantalla existe para no
   * hacer. Y en ese mismo escenario Tiendanube SÍ detectaba el fallo (la edge
   * function no responde), así que las dos tarjetas se contradecían a 30 cm una
   * de la otra. Mismo concepto que `MarcaPage` (`perfilFallo`).
   */
  const perfilFallo = !cargandoPerfil && profile === null;

  /** Reintenta las DOS cosas que pudieron fallar: el perfil (de donde sale el
   *  `id_company`) y las consultas de las fuentes. Va envuelto en una arrow sin
   *  argumentos a propósito: `refresh` recibe `silent` como primer parámetro y
   *  pasarlo pelado a un `onClick` le entregaría el evento del mouse (truthy). */
  const reintentar = () => {
    void refresh();
    fuentes.reintentar();
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {/* "Datos" POR PEDIDO EXPLÍCITO DE MATÍAS. NO REVERTIR ni "mejorar"
              a algo más descriptivo: la pantalla se llama así y su segunda
              sección se llama "Fuentes de datos". Sin bajada, tampoco por
              pedido suyo. */}
          <h1 className="text-[22px] font-semibold text-brand-black">Datos</h1>
        </div>
        <BotonPreguntaleAtlas
          pantalla="Fuentes de datos del estudio de marketing"
          detalle="Conexiones de las fuentes (Instagram, Meta Ads, Tiendanube) y base de conocimiento."
        />
      </div>

      <div className="mt-8">
        <SeccionConexiones
          fuentes={fuentes}
          cargandoPerfil={cargandoPerfil}
          perfilFallo={perfilFallo}
          onReintentar={reintentar}
        />
      </div>

      <section className="mt-10">
        {/* El h2 "Fuentes de datos" lo dibuja `CanalesReferencia`, no esta
            pantalla: Matías pidió el botón "Añadir fuente" a la misma altura
            que el título, y para eso los dos tienen que compartir la fila flex.
            NO subirlo de vuelta acá. El nombre es pedido suyo también (cambió
            de opinión sobre "Base de conocimiento"): NO REVERTIR. */}
        <CanalesReferencia brandId={currentBrandId} titulo="Fuentes de datos" />

        {/* El bloque "Más fuentes de conocimiento" que estaba acá lo sacó Matías
            por pedido explícito. NO REVERTIR. Era un `SeccionProximamente` que
            enumeraba lo que falta de la base de conocimiento (guía de estilo,
            cuentas de la competencia). */}
      </section>
    </div>
  );
};

export default ConfiguracionDatosPage;
